import {equals} from 'ramda';

import {getSafely, assignSafely} from 'shared/web';

import {GetTrimLevelsApiResponse, RelativeFilterResponseBody} from '../../../../../types/api/api';
import {FilterFormData} from '../../../../../types/FilterFormData';
import {PriceReportType} from '../../../../../types/PriceReportType';
import {type IsKnownPresetFeature} from './normalizePresetFeatures';
import {
  buildPresetApplyContext,
  PRESET_CONTROLLED_FORM_FIELDS,
  PresetControlledFormField,
  resolvePresetFormFieldValue,
} from './presetFieldResolvers';

const PRESET_ARRAY_FIELD_NAMES = [
  'transmission',
  'fuelType',
  'bodyStyle',
  'drive',
  'isVatDeductible',
  'trimLevels',
  'features',
] as const satisfies readonly PresetControlledFormField[];

type PresetArrayFieldName = (typeof PRESET_ARRAY_FIELD_NAMES)[number];

const PRESET_ARRAY_FIELDS = new Set<PresetControlledFormField>(PRESET_ARRAY_FIELD_NAMES);

function isPresetArrayField(field: PresetControlledFormField): field is PresetArrayFieldName {
  return PRESET_ARRAY_FIELDS.has(field);
}

function isCompletePresetSnapshot(
  snapshot: Partial<Pick<FilterFormData, PresetControlledFormField>>
): snapshot is Pick<FilterFormData, PresetControlledFormField> {
  return PRESET_CONTROLLED_FORM_FIELDS.every((field) => field in snapshot);
}

function sortStringLikeArray<T extends string>(values: readonly T[]): T[] {
  return [...values].sort();
}

function normalizePresetArrayFieldValue(
  field: PresetArrayFieldName,
  value: FilterFormData[PresetControlledFormField]
): FilterFormData[PresetArrayFieldName] {
  if (field === 'trimLevels') {
    if (!Array.isArray(value)) {
      return null;
    }

    return sortStringLikeArray(value);
  }

  if (!Array.isArray(value)) {
    return [];
  }

  return sortStringLikeArray(value);
}

export type ResolvePresetFormSnapshotParams = {
  priceReport: PriceReportType;
  trimLevelsCatalog?: GetTrimLevelsApiResponse['trim_levels'] | null;
  isKnownFeature?: IsKnownPresetFeature;
};

function normalizePresetFieldForCompare(
  field: PresetControlledFormField,
  value: FilterFormData[PresetControlledFormField]
): FilterFormData[PresetControlledFormField] {
  if (isPresetArrayField(field)) {
    return normalizePresetArrayFieldValue(field, value);
  }

  return value ?? null;
}

export function resolvePresetControlledFormSnapshot(
  config: RelativeFilterResponseBody['config'],
  params: ResolvePresetFormSnapshotParams
): Pick<FilterFormData, PresetControlledFormField> {
  const ctx = buildPresetApplyContext(
    config,
    params.priceReport,
    params.trimLevelsCatalog,
    params.isKnownFeature ?? (() => true)
  );

  const snapshot: Partial<Pick<FilterFormData, PresetControlledFormField>> = {};

  for (const field of PRESET_CONTROLLED_FORM_FIELDS) {
    assignSafely(snapshot, field, resolvePresetFormFieldValue(field, ctx));
  }

  if (!isCompletePresetSnapshot(snapshot)) {
    throw new Error('Preset snapshot is missing one or more controlled fields');
  }

  return snapshot;
}

export function arePresetControlledFormFieldsEqual(
  formValues: Pick<FilterFormData, PresetControlledFormField>,
  snapshot: Pick<FilterFormData, PresetControlledFormField>
): boolean {
  return PRESET_CONTROLLED_FORM_FIELDS.every((field) =>
    equals(
      normalizePresetFieldForCompare(field, getSafely(formValues, field)),
      normalizePresetFieldForCompare(field, getSafely(snapshot, field))
    )
  );
}

function comparePresetMatchPriority(
  left: RelativeFilterResponseBody,
  right: RelativeFilterResponseBody
): number {
  const [leftDefault, leftSystem, leftPosition] = presetMatchPriority(left);
  const [rightDefault, rightSystem, rightPosition] = presetMatchPriority(right);

  if (leftDefault !== rightDefault) {
    return leftDefault - rightDefault;
  }
  if (leftSystem !== rightSystem) {
    return leftSystem - rightSystem;
  }
  return leftPosition - rightPosition;
}

function presetMatchPriority(preset: RelativeFilterResponseBody): [number, number, number] {
  return [preset.default ? 0 : 1, preset.system ? 1 : 0, preset.position];
}

export function findMatchingPreset(
  presets: RelativeFilterResponseBody[],
  formValues: Pick<FilterFormData, PresetControlledFormField>,
  params: ResolvePresetFormSnapshotParams
): RelativeFilterResponseBody | null {
  const matches = presets.filter((preset) =>
    arePresetControlledFormFieldsEqual(
      formValues,
      resolvePresetControlledFormSnapshot(preset.config, params)
    )
  );

  if (matches.length === 0) {
    return null;
  }

  return [...matches].sort(comparePresetMatchPriority)[0] ?? null;
}
